/**
 * 体験デモのアクセス元（運営管理「操作ログ」のタブ）
 * 体験デモへのアクセスを「訪問」単位で一覧し、IPアドレスから推定した地域（国・都道府県・市区町村）や
 * 回線事業者を選んで絞り込む。訪問を開くと、ログイン・操作・見た機能を時系列で見られる。
 * 社内利用のみ。位置はIPからの推定で、携帯回線は通信会社の拠点が出る（目安にならない）。
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAccessCompanies, getAccessSummary, getAccessVisits, getAccessEvents, exportAccessVisits, getIpRegistry,
  getInternalNetworks, addInternalNetwork, renameInternalNetwork, deleteInternalNetwork, InternalNetworks,
  AccessLogCompany, AccessSummary, AccessVisit, AccessEvent, AccessFilter, IpRegistry,
} from '../services/api';
import { fmtDateTimeSecJst, parseUtc } from '../utils/datetime';

const APP_LABEL: Record<string, string> = {
  admin: '管理サイト', crew_shared: '船員アプリ（共有端末）', crew_personal: '船員アプリ（個人）', other: 'その他',
};
const KIND_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  open:         { bg: '#f1f5f9', fg: '#475569', label: '開始' },
  login:        { bg: '#dbeafe', fg: '#1d4ed8', label: 'ログイン' },
  login_failed: { bg: '#fee2e2', fg: '#b91c1c', label: '失敗' },
  logout:       { bg: '#f1f5f9', fg: '#475569', label: '終了' },
  operation:    { bg: '#dcfce7', fg: '#15803d', label: '操作' },
  view:         { bg: '#fef9c3', fg: '#a16207', label: '閲覧' },
};
const PAGE = 50;

// 回線の種類（サーバーの NET_TYPE_LABEL と同じキー）。色で一目で分かるようにする
const NET_STYLE: Record<string, { bg: string; fg: string; short: string }> = {
  org:      { bg: '#dcfce7', fg: '#166534', short: '独自ドメイン' },
  business: { bg: '#dbeafe', fg: '#1e40af', short: '法人向け固定回線の可能性' },
  consumer: { bg: '#f1f5f9', fg: '#475569', short: '一般回線（光など）' },
  mobile:   { bg: '#fef3c7', fg: '#92400e', short: '📱 携帯回線' },
  cloud:    { bg: '#fee2e2', fg: '#991b1b', short: 'クラウド・VPN' },
  unknown:  { bg: '#f5f3ff', fg: '#5b21b6', short: '逆引きなし' },
};

const jstYmd = (offsetDays = 0) => new Date(Date.now() + 9 * 3600_000 - offsetDays * 86400_000).toISOString().slice(0, 10);

function placeLabel(v: { country: string; country_code: string | null; region: string | null; city: string | null }): string {
  if (v.country_code === 'LAN') return '社内・閉域ネットワーク';
  if (v.country === '未判定') return '未判定（地理情報の取得待ち）';
  const parts = v.country_code === 'JP' ? [v.region, v.city] : [v.country, v.region, v.city];
  return parts.filter(Boolean).join(' ') || v.country;
}

function stay(a: string, b: string): string {
  const s = parseUtc(a), e = parseUtc(b);
  if (!s || !e) return '';
  const m = Math.max(0, Math.round((e.getTime() - s.getTime()) / 60000));
  return m < 1 ? '1分未満' : m < 60 ? `${m}分` : `${Math.floor(m / 60)}時間${m % 60}分`;
}

const sel: React.CSSProperties = { border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '6px 10px', fontSize: 13, background: '#fff', outline: 'none' };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 };
const td: React.CSSProperties = { padding: '9px 12px', verticalAlign: 'top', borderBottom: '1px solid #f1f5f9' };

export default function DemoAccessPanel() {
  const [companies, setCompanies] = useState<AccessLogCompany[]>([]);
  const [f, setF] = useState<AccessFilter>({ from: jstYmd(29), to: jstYmd(0) });
  const [summary, setSummary] = useState<AccessSummary | null>(null);
  const [visits, setVisits] = useState<AccessVisit[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [events, setEvents] = useState<Record<string, AccessEvent[]>>({});
  const [exporting, setExporting] = useState(false);
  const [registry, setRegistry] = useState<Record<string, IpRegistry | 'loading' | { error: string }>>({});
  const [netOpen, setNetOpen] = useState(false);
  const lookupRegistry = async (ip: string) => {
    setRegistry(p => ({ ...p, [ip]: 'loading' }));
    try { const r = await getIpRegistry(ip); setRegistry(p => ({ ...p, [ip]: r })); }
    catch (e) { setRegistry(p => ({ ...p, [ip]: { error: e instanceof Error ? e.message : '照会に失敗しました' } })); }
  };

  useEffect(() => {
    getAccessCompanies().then(cs => {
      setCompanies(cs);
      const def = cs.find(c => c.subdomain === 'test') ?? cs.find(c => c.enabled) ?? cs[0];
      if (def) setF(p => ({ ...p, company_id: def.id }));
    }).catch(e => setError(e instanceof Error ? e.message : '会社一覧の取得に失敗しました'));
  }, []);

  const load = useCallback(async (filter: AccessFilter, p: number) => {
    if (!filter.company_id) return;
    setLoading(true); setError('');
    try {
      const [s, v] = await Promise.all([getAccessSummary(filter), getAccessVisits({ ...filter, limit: PAGE, offset: p * PAGE })]);
      setSummary(s); setVisits(v.data); setTotal(v.total);
    } catch (e) { setError(e instanceof Error ? e.message : '取得に失敗しました'); }
    setLoading(false);
  }, []);
  useEffect(() => { void load(f, page); }, [f, page, load]);

  const set = (patch: AccessFilter) => { setPage(0); setOpen(null); setF(p => ({ ...p, ...patch })); };

  // 住所の選択肢（国 → 都道府県 → 市区町村）。件数は地域の絞り込みを外した条件で数えている
  const places = summary?.places ?? [];
  const countries = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of places) m.set(p.country, (m.get(p.country) ?? 0) + p.visits);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [places]);
  const regions = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of places) if (!f.country || p.country === f.country) m.set(p.region, (m.get(p.region) ?? 0) + p.visits);
    return [...m.entries()].filter(([r]) => r).sort((a, b) => b[1] - a[1]);
  }, [places, f.country]);
  const cities = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of places) if ((!f.country || p.country === f.country) && (!f.region || p.region === f.region)) m.set(p.city, (m.get(p.city) ?? 0) + p.visits);
    return [...m.entries()].filter(([c]) => c).sort((a, b) => b[1] - a[1]);
  }, [places, f.country, f.region]);
  const topPlaces = useMemo(() => [...places].sort((a, b) => b.visits - a.visits).slice(0, 12), [places]);

  const toggle = async (v: AccessVisit) => {
    if (open === v.id) { setOpen(null); return; }
    setOpen(v.id);
    if (!events[v.id]) {
      try { const ev = await getAccessEvents(v.id); setEvents(p => ({ ...p, [v.id]: ev })); } catch { /* noop */ }
    }
  };

  const doExport = async () => {
    setExporting(true);
    try { await exportAccessVisits(f); } catch (e) { setError(e instanceof Error ? e.message : 'CSVの出力に失敗しました'); }
    setExporting(false);
  };

  const t = summary?.totals;
  const totalPages = Math.ceil(total / PAGE);
  const chip = (active: boolean): React.CSSProperties => ({
    border: `1.5px solid ${active ? '#2563eb' : '#e2e8f0'}`, background: active ? '#eff6ff' : '#fff', color: active ? '#1d4ed8' : '#334155',
    borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  });

  return (
    <div>
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 14px', fontSize: 12, color: '#475569', marginBottom: 14, lineHeight: 1.7 }}>
        体験デモへのアクセスを、IPアドレスから推定した地域・回線事業者とあわせて記録しています（社内利用のみ）。
        位置は<b>市区町村レベルの推定</b>で、<b>📱携帯回線は通信会社の拠点（東京・大阪など）が出るため目安になりません</b>。
        <b>当社の環境からのアクセスも表示し、紫の「当社」に判定の理由（登録した回線の名前／運営管理者のログイン元）を出します。</b>
        「当社以外」に切り替えると見込み客のアクセスだけになります。ボットは既定で除いています。
        回線の種類・ドメインは公開DNSの逆引きからの推定で、<b>緑の「独自ドメイン」は組織名が分かることがあります</b>（逆引きが無い固定IPも法人に多い）。
        {summary?.geo?.month
          ? <span style={{ color: '#94a3b8' }}>　地理データ: DB-IP Lite {summary.geo.month}（CC BY 4.0, db-ip.com）</span>
          : <span style={{ color: '#b45309' }}>　地理データは取得中です（サーバー起動後に自動で取得します）。</span>}
        {(summary?.geo_pending ?? 0) > 0 && <span style={{ color: '#b45309' }}>　地域の判定待ち {summary!.geo_pending} 件（数十秒で反映されます）</span>}
      </div>

      {/* 条件 */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '12px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={lbl}>会社</div>
            <select value={f.company_id ?? ''} onChange={e => set({ company_id: e.target.value, country: '', region: '', city: '', isp: '' })} style={{ ...sel, minWidth: 220 }}>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}{c.enabled ? '' : '（記録停止中）'}</option>)}
            </select>
          </div>
          <div>
            <div style={lbl}>期間（日本時間）</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="date" value={f.from ?? ''} onChange={e => set({ from: e.target.value })} style={sel} />
              <span style={{ color: '#94a3b8' }}>〜</span>
              <input type="date" value={f.to ?? ''} onChange={e => set({ to: e.target.value })} style={sel} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, paddingBottom: 2 }}>
            {[['今日', 0], ['7日', 6], ['30日', 29], ['90日', 89], ['1年', 364]].map(([l, d]) => (
              <button key={String(l)} onClick={() => set({ from: jstYmd(Number(d)), to: jstYmd(0) })} style={chip(f.from === jstYmd(Number(d)) && f.to === jstYmd(0))}>{l}</button>
            ))}
          </div>
          <div>
            <div style={lbl}>アプリ</div>
            <select value={f.app ?? ''} onChange={e => set({ app: e.target.value })} style={sel}>
              <option value="">すべて</option>
              {Object.entries(APP_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <div style={lbl}>IPアドレス</div>
            <input value={f.ip ?? ''} onChange={e => set({ ip: e.target.value })} placeholder="一部でも可" style={{ ...sel, width: 150 }} />
          </div>
          <div>
            <div style={lbl}>ユーザー</div>
            <input value={f.user ?? ''} onChange={e => set({ user: e.target.value })} placeholder="名前・社員コード" style={{ ...sel, width: 150 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: '#334155' }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: '#64748b', fontSize: 11 }}>当社の環境から</span>
              {([['', 'すべて'], ['exclude', '当社以外'], ['only', '当社のみ']] as Array<[string, string]>).map(([k, l]) => (
                <button key={k} onClick={() => set({ internal: k })} style={{ ...chip((f.internal ?? '') === k), padding: '2px 9px', fontSize: 11.5 }}>{l}</button>
              ))}
              <button onClick={() => setNetOpen(true)} style={{ ...chip(false), padding: '2px 9px', fontSize: 11.5, color: '#6d28d9', borderColor: '#ddd6fe' }}>当社の回線を登録・確認</button>
            </div>
            <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={f.only_login === '1'} onChange={e => set({ only_login: e.target.checked ? '1' : '' })} />ログインまでした訪問だけ
            </label>
          </div>
          <button onClick={() => void load(f, page)} style={{ ...sel, cursor: 'pointer', fontWeight: 700, marginLeft: 'auto' }}>更新</button>
          <button onClick={() => void doExport()} disabled={exporting} style={{ ...sel, cursor: 'pointer', fontWeight: 700 }}>{exporting ? '出力中…' : 'CSV'}</button>
        </div>

        {/* 住所で選ぶ */}
        <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 12, paddingTop: 10 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', paddingBottom: 6 }}>📍 住所で選ぶ</div>
            <div>
              <div style={lbl}>国</div>
              <select value={f.country ?? ''} onChange={e => set({ country: e.target.value, region: '', city: '' })} style={{ ...sel, minWidth: 140 }}>
                <option value="">すべての国</option>
                {countries.map(([c, n]) => <option key={c} value={c}>{c}（{n}）</option>)}
              </select>
            </div>
            <div>
              <div style={lbl}>都道府県・地域</div>
              <select value={f.region ?? ''} onChange={e => set({ region: e.target.value, city: '' })} style={{ ...sel, minWidth: 160 }}>
                <option value="">すべて</option>
                {regions.map(([r, n]) => <option key={r} value={r}>{r}（{n}）</option>)}
              </select>
            </div>
            <div>
              <div style={lbl}>市区町村</div>
              <select value={f.city ?? ''} onChange={e => set({ city: e.target.value })} style={{ ...sel, minWidth: 160 }}>
                <option value="">すべて</option>
                {cities.map(([c, n]) => <option key={c} value={c}>{c}（{n}）</option>)}
              </select>
            </div>
            <div>
              <div style={lbl}>回線事業者</div>
              <select value={f.isp ?? ''} onChange={e => set({ isp: e.target.value })} style={{ ...sel, minWidth: 200, maxWidth: 280 }}>
                <option value="">すべて</option>
                {(summary?.isps ?? []).filter(i => i.isp).map(i => <option key={i.isp} value={i.isp}>{i.is_mobile ? '📱 ' : ''}{i.isp}（{i.visits}）</option>)}
              </select>
            </div>
            <div>
              <div style={lbl}>回線の種類（推定）</div>
              <select value={f.net ?? ''} onChange={e => set({ net: e.target.value })} style={{ ...sel, maxWidth: 260 }}>
                <option value="">すべて</option>
                {(summary?.nets ?? []).filter(n => n.net_type).map(n => (
                  <option key={n.net_type} value={n.net_type}>{summary?.net_labels?.[n.net_type] ?? n.net_type}（{n.visits}）</option>
                ))}
              </select>
            </div>
            <div>
              <div style={lbl}>組織のドメイン</div>
              <select value={f.domain ?? ''} onChange={e => set({ domain: e.target.value })} style={{ ...sel, minWidth: 180, maxWidth: 260 }}>
                <option value="">すべて</option>
                {(summary?.domains ?? []).map(d => <option key={d.domain} value={d.domain}>{d.domain}（{d.visits}）</option>)}
              </select>
            </div>
            {(f.country || f.region || f.city || f.isp || f.mobile || f.net || f.domain) && (
              <button onClick={() => set({ country: '', region: '', city: '', isp: '', mobile: '', net: '', domain: '' })} style={{ ...chip(false), marginBottom: 2 }}>住所・回線の条件を外す</button>
            )}
          </div>
          {topPlaces.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {topPlaces.map(p => {
                const active = f.country === p.country && (f.region ?? '') === p.region && (f.city ?? '') === p.city;
                return (
                  <button key={`${p.country}|${p.region}|${p.city}`} style={chip(active)}
                    onClick={() => set(active ? { country: '', region: '', city: '' } : { country: p.country, region: p.region, city: p.city })}
                    title={`${p.ips}件のIPアドレス／ログインした訪問 ${p.login_visits}件${p.mobile_visits ? `／うち携帯回線 ${p.mobile_visits}件` : ''}`}>
                    {placeLabel({ country: p.country, country_code: p.country_code, region: p.region, city: p.city })}
                    {p.mobile_visits === p.visits && p.visits > 0 ? ' 📱' : ''}　{p.visits}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#dc2626', fontSize: 13 }}>{error}</div>}

      {/* 合計 */}
      {t && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 12 }}>
          {[['訪問', t.visits], ['IPアドレス', t.ips], ['ログインまでした訪問', t.login_visits], ['操作（登録・変更・打刻）', t.ops]].map(([l, n], i) => (
            <div key={String(l)} style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>{l}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{Number(n ?? 0).toLocaleString()}</div>
              {i === 0 && (t.internal_visits ?? 0) > 0 && (
                <div style={{ fontSize: 11, color: '#6d28d9', fontWeight: 700 }} title={(summary?.internal_breakdown ?? []).map(b => `${b.label} ${b.visits}`).join(' / ')}>
                  うち当社 {t.internal_visits}（{(summary?.internal_breakdown ?? []).map(b => `${b.label} ${b.visits}`).join('・')}）
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 訪問の一覧 */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading && visits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>読み込み中...</div>
        ) : visits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>この条件のアクセスはありません</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['日時（滞在）', '場所（推定）', '回線事業者', 'IPアドレス', '端末・アプリ', 'ユーザー', '操作'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 12px', color: '#64748b', fontWeight: 700, fontSize: 11.5, borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visits.map(v => {
                const isOpen = open === v.id;
                return (
                  <Fragment key={v.id}>
                    <tr onClick={() => void toggle(v)} style={{ cursor: 'pointer', background: isOpen ? '#f8fafc' : undefined }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{fmtDateTimeSecJst(v.started_at)}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{stay(v.started_at, v.last_seen_at)}・{v.hits}回の通信</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{placeLabel(v)}</div>
                        {v.host_hint && <div style={{ fontSize: 11, color: '#0f766e' }}>ホスト名: {v.host_hint}</div>}
                        <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                          {v.is_mobile ? <span style={{ fontSize: 10.5, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '1px 7px' }}>📱 携帯回線（位置は目安外）</span> : null}
                          {v.is_internal ? <span style={{ fontSize: 10.5, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', borderRadius: 999, padding: '1px 7px' }}>当社：{v.internal_label ?? '当社の環境'}</span> : null}
                        </div>
                      </td>
                      <td style={{ ...td, color: '#475569', maxWidth: 240 }}>
                        <div>{v.isp ?? '—'}</div>
                        {v.net_type && NET_STYLE[v.net_type] && (
                          <span style={{ display: 'inline-block', marginTop: 2, fontSize: 10.5, fontWeight: 700, background: NET_STYLE[v.net_type]!.bg, color: NET_STYLE[v.net_type]!.fg, borderRadius: 999, padding: '1px 7px' }}
                            title={summary?.net_labels?.[v.net_type] ?? ''}>{NET_STYLE[v.net_type]!.short}</span>
                        )}
                        {v.ptr_domain && (v.net_type === 'org' || v.net_type === 'business') && (
                          <div style={{ fontSize: 12, fontWeight: 800, color: '#166534', marginTop: 2 }}>{v.ptr_domain}</div>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: 'Consolas, monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {v.ip}
                        {v.ip_visits > 1 && <div style={{ fontFamily: 'inherit', fontSize: 10.5, color: '#b45309', fontWeight: 700 }}>このIPから計{v.ip_visits}回</div>}
                      </td>
                      <td style={td}>
                        <div style={{ color: '#334155' }}>{v.device ?? '—'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{APP_LABEL[v.app ?? ''] ?? v.app ?? '—'}</div>
                      </td>
                      <td style={td}>
                        {v.user_name
                          ? <><div style={{ fontWeight: 700, color: '#0f172a' }}>{v.user_name}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{v.employee_code ?? ''}</div></>
                          : <span style={{ color: '#94a3b8' }}>ログイン前に離脱</span>}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#334155' }}>
                        ログイン {v.logins}／操作 {v.ops}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ padding: '10px 16px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 420px' }}>
                              <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>この訪問での出来事</div>
                              {(events[v.id] ?? []).length === 0
                                ? <div style={{ fontSize: 12, color: '#94a3b8' }}>読み込み中…</div>
                                : (events[v.id] ?? []).map(e => {
                                    const k = KIND_STYLE[e.kind] ?? KIND_STYLE['view']!;
                                    return (
                                      <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '3px 0' }}>
                                        <span style={{ color: '#94a3b8', fontFamily: 'Consolas, monospace', minWidth: 140 }}>{fmtDateTimeSecJst(e.created_at)}</span>
                                        <span style={{ background: k.bg, color: k.fg, borderRadius: 999, padding: '0 8px', fontSize: 10.5, fontWeight: 800 }}>{k.label}</span>
                                        <span style={{ color: e.status >= 400 ? '#b91c1c' : '#1e293b', fontWeight: 600 }}>{e.label}{e.status >= 400 ? `（失敗 ${e.status}）` : ''}</span>
                                        {e.detail && <span style={{ color: '#64748b' }}>{e.detail}</span>}
                                      </div>
                                    );
                                  })}
                            </div>
                            <div style={{ flex: '0 1 360px', fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
                              <div><b>場所（推定）</b> {placeLabel(v)}{v.is_mobile ? '（携帯回線のため目安外）' : ''}</div>
                              {v.host_hint && <div><b>ホスト名の地域</b> {v.host_hint}</div>}
                              <div><b>回線</b> {v.isp ?? '—'}{v.asn ? `（AS${v.asn}）` : ''}</div>
                              <div><b>回線の種類（推定）</b> {v.net_type ? (summary?.net_labels?.[v.net_type] ?? v.net_type) : '判定中'}</div>
                              <div><b>逆引きホスト名</b> <span style={{ fontFamily: 'Consolas, monospace', wordBreak: 'break-all' }}>{v.ptr ?? (v.net_type ? 'なし' : '判定中')}</span></div>
                              {v.ptr_domain && <div><b>ドメイン</b> {v.ptr_domain}</div>}
                              <div><b>IPアドレス</b> <span style={{ fontFamily: 'Consolas, monospace' }}>{v.ip}</span></div>
                              <div><b>当社の環境か</b> {v.is_internal ? <span style={{ color: '#6d28d9', fontWeight: 700 }}>当社（{v.internal_label}）</span> : '当社の回線としては未登録'}</div>
                              <div><b>このIPからの訪問</b> 計{v.ip_visits}回{v.ip_first_seen ? `（初回 ${fmtDateTimeSecJst(v.ip_first_seen)}）` : ''}</div>
                              {v.ip_users && <div><b>このIPで使われたアカウント</b> {v.ip_users.split(',').join('、')}</div>}
                              <div><b>最終アクセス</b> {fmtDateTimeSecJst(v.last_seen_at)}</div>
                              {v.country_code !== 'LAN' && (() => {
                                const r = registry[v.ip];
                                return (
                                  <div style={{ marginTop: 6, padding: '8px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                    <button onClick={ev => { ev.stopPropagation(); void lookupRegistry(v.ip); }} disabled={r === 'loading'}
                                      style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                      {r === 'loading' ? '照会中…' : 'IPアドレスの登録情報を照会'}
                                    </button>
                                    <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>
                                      JPNIC・RDAPの公開データベースへの照会です。JPNICの利用条件は「ネットワーク管理目的」に限られるため、
                                      不審なアクセスの確認などに使い、営業リストづくりには使わないでください。結果は保存せず、照会したことは操作ログに残ります。
                                    </div>
                                    {r && r !== 'loading' && 'error' in r && <div style={{ color: '#b91c1c', marginTop: 4 }}>{r.error}</div>}
                                    {r && r !== 'loading' && !('error' in r) && (
                                      <div style={{ marginTop: 6 }}>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>出典: {r.source}</div>
                                        {Object.keys(r.fields).length === 0
                                          ? <div style={{ color: '#94a3b8' }}>{r.note ?? '登録情報がありません'}</div>
                                          : Object.entries(r.fields).map(([k, val]) => <div key={k}><b>{k}</b> {val}</div>)}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {v.latitude != null && v.longitude != null && v.country_code !== 'LAN' && (
                                <div>
                                  <a href={`https://www.openstreetmap.org/?mlat=${v.latitude}&mlon=${v.longitude}#map=12/${v.latitude}/${v.longitude}`}
                                    target="_blank" rel="noreferrer noopener" style={{ color: '#2563eb', fontWeight: 700 }}>地図で見る（おおよその位置）</a>
                                </div>
                              )}
                              <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', wordBreak: 'break-all' }}>{v.user_agent}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {netOpen && <InternalNetworksModal onClose={() => { setNetOpen(false); void load(f, page); }} />}

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ ...sel, cursor: 'pointer' }}>← 前へ</button>
          <span style={{ fontSize: 13, color: '#64748b' }}>{page + 1} / {totalPages}（{total}件）</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ ...sel, cursor: 'pointer' }}>次へ →</button>
        </div>
      )}
    </div>
  );
}

/** 当社の回線（社内ネットワーク）を登録・確認するダイアログ */
function InternalNetworksModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<InternalNetworks | null>(null);
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const reload = () => getInternalNetworks().then(setData).catch(e => setErr(e instanceof Error ? e.message : '取得に失敗しました'));
  useEffect(() => { void reload(); }, []);
  const add = async (v = value, l = label) => {
    setBusy(true); setErr('');
    try { await addInternalNetwork(v, l); setValue(''); setLabel(''); await reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : '登録に失敗しました'); }
    setBusy(false);
  };
  const registeredIps = new Set((data?.registered ?? []).flatMap(r => r.kind === 'ip' ? [r.value] : r.resolved));
  const inp: React.CSSProperties = { border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '7px 10px', fontSize: 13 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(760px, 100%)', maxHeight: '88vh', overflow: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>当社の回線（社内ネットワーク）</div>
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.7, marginBottom: 14 }}>
          ここに登録した回線からのアクセスは、一覧で「当社：（名前）」と表示されます。IPアドレス、範囲（例: 203.0.113.0/28）、
          ホスト名（例: ○○.netvolante.jp などのDDNS。10分ごとに引き直してIPの変化に追従）を登録できます。
          運営管理者がログインしたIP（直近180日）は登録しなくても自動で当社と判定します（携帯回線のIPは他人と共用なので除きます）。
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <div style={{ flex: '1 1 220px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>IPアドレス・範囲・ホスト名</div>
            <input value={value} onChange={e => setValue(e.target.value)} placeholder="例: 143.189.211.94 / unison-test.aa1.netvolante.jp" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>名前</div>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="例: 本社（NURO光）" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <button onClick={() => void add()} disabled={busy || !value.trim() || !label.trim()}
            style={{ ...inp, background: '#6d28d9', color: '#fff', border: 'none', fontWeight: 800, cursor: 'pointer', opacity: busy || !value.trim() || !label.trim() ? 0.5 : 1 }}>登録</button>
        </div>
        {err && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

        <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', margin: '8px 0 6px' }}>登録済み</div>
        {(data?.registered ?? []).length === 0
          ? <div style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 12 }}>まだありません</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 14 }}>
              <tbody>
                {data!.registered.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '7px 6px', fontWeight: 800, color: '#6d28d9' }}>{r.label}</td>
                    <td style={{ padding: '7px 6px', fontFamily: 'Consolas, monospace' }}>
                      {r.value}
                      {r.kind === 'host' && <div style={{ fontSize: 11, color: '#64748b' }}>現在のIP: {r.resolved.length ? r.resolved.join(', ') : '（引けませんでした）'}</div>}
                    </td>
                    <td style={{ padding: '7px 6px', color: '#94a3b8', fontSize: 11 }}>{r.kind === 'ip' ? 'IP' : r.kind === 'cidr' ? '範囲' : 'ホスト名'}</td>
                    <td style={{ padding: '7px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={async () => { const n = window.prompt('新しい名前', r.label); if (n && n.trim()) { await renameInternalNetwork(r.id, n.trim()).catch(e => setErr(String(e?.message ?? e))); await reload(); } }}
                        style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, padding: '3px 8px', fontSize: 11.5, cursor: 'pointer', marginRight: 4 }}>名前を変更</button>
                      <button onClick={async () => { if (window.confirm(`「${r.label}」（${r.value}）の登録を削除しますか？`)) { await deleteInternalNetwork(r.id).catch(e => setErr(String(e?.message ?? e))); await reload(); } }}
                        style={{ border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', borderRadius: 6, padding: '3px 8px', fontSize: 11.5, cursor: 'pointer' }}>削除</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', margin: '8px 0 6px' }}>自動判定（運営管理者のログイン元・直近180日）</div>
        {(data?.auto ?? []).length === 0
          ? <div style={{ fontSize: 12.5, color: '#94a3b8' }}>ありません</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {data!.auto.map(a => (
                  <tr key={a.ip} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px', fontFamily: 'Consolas, monospace' }}>{a.ip}</td>
                    <td style={{ padding: '6px', color: '#64748b' }}>
                      最終ログイン {fmtDateTimeSecJst(a.last_at)}
                      {a.isp && <div style={{ fontSize: 11, color: '#94a3b8' }}>{a.isp}</div>}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>
                      {registeredIps.has(a.ip)
                        ? <span style={{ fontSize: 11, color: '#94a3b8' }}>登録済み</span>
                        : a.is_mobile
                        ? <span style={{ fontSize: 11, color: '#92400e' }} title="携帯回線のIPは大勢で共用され、すぐ別の人に割り当て直されるため">📱 携帯回線のため当社扱いにしません</span>
                        : <button onClick={() => { const n = window.prompt(`${a.ip} に付ける名前（例: 本社 NURO光）`); if (n && n.trim()) void add(a.ip, n.trim()); }}
                            style={{ border: '1px solid #ddd6fe', background: '#fff', color: '#6d28d9', borderRadius: 6, padding: '3px 8px', fontSize: 11.5, cursor: 'pointer' }}>名前を付けて登録</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
